export const en: Record<string, string> = {
  // Header
  'app.title': 'Loto Caller',
  'header.settings': 'Settings',

  // Controls
  'controls.nextNumber': 'Next Number',
  'controls.startGame': 'Start Game',
  'controls.autoPlay': 'Auto Play',
  'controls.reset': 'Reset',
  'controls.nextIn': 'Next in: {seconds}s',
  'controls.previous': 'Previous',

  // Display
  'display.ready': 'Ready',
  'display.playing': '🔊 Playing...',
  'display.skipAudio': 'Skip Audio',

  // Settings modal
  'settings.title': 'Settings',
  'settings.language': 'Audio Language',
  'settings.style': 'Style',
  'settings.range': 'Number Range',
  'settings.min': 'Minimum',
  'settings.max': 'Maximum',
  'settings.interval': 'Interval (seconds)',
  'settings.apply': 'Apply',
  'settings.cancel': 'Cancel',
  'settings.uiLanguage': 'UI Language',
  'settings.preview': 'Preview',
  'settings.rangeLocked': 'Cannot change range during active game. Reset to adjust.',
  'settings.hotSwitchWarning': '⚠️ Changing style mid-game will affect next number only',

  // Modals
  'reset.title': '⚠️ Reset Game?',
  'reset.message': 'All progress will be lost.',
  'reset.confirm': 'Reset',
  'complete.title': '🎉 All Numbers Called!',
  'complete.numbersCalled': 'Numbers Called: {count}',
  'complete.timeElapsed': 'Time Elapsed: {time}',
  'complete.style': 'Style: {style}',
  'complete.viewHistory': 'View History',
  'complete.newGame': 'New Game',
  'history.title': '📜 Call History',
  'history.exportCsv': 'Export CSV',
  'history.close': 'Close',
  'history.order': '#',
  'history.number': 'Number',

  // Status
  'status.called': 'Called: {called}/{total}',
  'status.time': 'Time: {time}',

  // Toasts
  'toast.settingsSaved': 'Settings saved',
  'toast.styleChanged': 'Style changed. Next number will use new style.',
  'toast.audioUnavailable': 'Audio unavailable for number {number}',
  'toast.autoPlayPaused': 'Auto Play paused',
  'toast.autoPlayResumed': 'Auto Play resumed',
  'toast.audioBlocked': '🔇 Audio blocked by browser. Click to enable.',
  'toast.connectionLost': '⚠️ Lost network connection. Audio may not load.',
  'toast.connectionRestored': 'Connection restored',
  'toast.storageLimitReached': '⚠️ Storage limit reached',
  'toast.corruptedData': 'Previous session data corrupted. Starting fresh.',
  'toast.pleaseWait': '⏳ Please wait for current audio to finish',
  'toast.previewNotAvailable': 'Preview not available for this style',
  'toast.previewFailed': 'Failed to play preview',
  'toast.previewPlaying': '🔊 Playing preview...',

  // Emergency
  'emergency.title': '⚠️ Running in Emergency Mode',
  'emergency.message': 'Audio library unavailable. Using limited configuration.',
  'emergency.retry': 'Retry',

  // Accessibility
  'a11y.numberCalled': 'Number {number} called',
  'a11y.gameComplete': 'Game complete. {count} numbers called. Time: {time}.',
  'a11y.skipToContent': 'Skip to main content',
  'a11y.gridLabel': 'Loto number board',
  'a11y.cellCalled': 'Number {number}, called',
  'a11y.cellNotCalled': 'Number {number}, not called',

  // Keyboard help
  'keyboard.title': 'Keyboard Shortcuts',
  'keyboard.next': 'Next number',
  'keyboard.autoplay': 'Toggle Auto Play',
  'keyboard.settings': 'Open settings',
  'keyboard.reset': 'Reset game',
  'keyboard.close': 'Close dialog / Skip audio',
  'keyboard.help': 'Show keyboard shortcuts',

  // Audio errors
  'error.consecutiveFailures': '⚠️ Audio Playback Error\nMultiple audio files failed to load.',
  'error.checkConnection': 'Check Connection',
  'error.continueSilent': 'Continue Without Audio',

  // Multi-tab
  'multitab.stateChanged': '⚠️ Game state was updated in another tab.',
  'multitab.reload': 'Reload Now',

  // Guideline
  'header.guideline': 'Guideline',
  'guideline.title': '📖 Guideline',
  'guideline.aboutTitle': 'About Loto',
  'guideline.aboutContent': 'Lô Tô (Loto) is a beloved traditional Vietnamese game, similar to Bingo. It originated in Southern Vietnam and is commonly played at festivals, fairs, community gatherings, and family events. A caller announces numbers from 1 to 90 one at a time — often singing them out in a distinctive, melodic chanting style — while players mark matching numbers on their cards. The game is known for its vibrant atmosphere, lively music, and community spirit.',
  'guideline.howToWinTitle': 'How to Win',
  'guideline.howToWinContent': 'Each player receives a Loto card containing a selection of numbers between 1 and 99 (or others). As the caller announces each number, players check their cards and mark any matching number. The first player to complete a full row, column, or the entire card (depending on the agreed rules) shouts "Loto!" and wins the round. Some variations require completing specific patterns for different prize tiers.',
  'guideline.howToUseTitle': 'How to Use This Website',
  'guideline.howToUseContent': 'This website serves as a digital Loto number caller. Here is how to use it:\n\n• Click "Start Game" or press N/Space to call the first number.\n• Numbers are called one at a time with audio announcements.\n• Use "Auto Play" or press A to have numbers called automatically at a set interval.\n• Open Settings (⚙️) to choose the audio language, calling style, number range, and auto-play speed.\n• The number grid highlights all called numbers for easy tracking.\n• Click "Reset" or press R to start a new game.\n• Press ? to see all keyboard shortcuts.',
  'guideline.close': 'Close',

  // Donate
  'donate.button': 'Donate',
  'donate.title': '❤️ Donate',
  'donate.thankYou': 'Thank you for your support!',
  'donate.message': 'If you find this app useful, you can support us by scanning the QR code below. Every contribution helps us maintain and improve the app.',
  'donate.scanQr': 'Scan QR code to donate',
  'donate.close': 'Close',

  // Terms of Service
  'terms.link': 'Terms of Service',
  'terms.title': 'Terms of Service',
  'terms.section1Title': '1. Purpose of the Website',
  'terms.section1Content': 'This website provides an online lotto-calling tool for entertainment and community activities.\nThe website does not operate or organize any gambling or betting activities.',
  'terms.section2Title': '2. Prohibited Activities',
  'terms.section2Content': 'Users are strictly prohibited from using the website for gambling, betting, or wagering money or valuable assets in any form.',
  'terms.section3Title': '3. User Responsibility',
  'terms.section3Content': 'Users are fully responsible for their own use of the website.',
  'terms.section4Title': '4. Disclaimer',
  'terms.section4Content': 'The website owner only provides a neutral technical tool and does not participate in, control, or profit from any illegal activities conducted by users.',
  'terms.section5Title': '5. General Terms',
  'terms.section5Content': 'Continued use of the website constitutes acceptance of these Terms of Service.',
  'terms.close': 'Close',
};
